import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import ReactModal from 'react-modal';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Grid, Typography } from '@material-ui/core';
import _ from 'lodash';
import { QRCodeSVG } from 'qrcode.react';
import { mobilizeiAppPublicationsService } from '../_services/mobilizei/appPublications.service';
import { mapExpoAccountNameToUrlSlug } from '../_helpers/expoAccountMapper';

ReactModal.setAppElement('#root');

const customStyles = {
    overlay: { backgroundColor: 'rgba(10, 10, 10, 0.45)', zIndex: 999 },
    content: { height: '80%', width: 'auto', maxWidth: '60%', margin: 'auto', overflowY: 'auto' },
};

const useStyles = makeStyles(() => ({
    title: { marginTop: 0 },
    sectionText: { fontSize: 16, color: '#3C4252' },
    actions: { display: 'flex', justifyContent: 'center', gap: 20, marginTop: 40, paddingBottom: 16 },
}));

function Instructions({ publication }) {
    const classes = useStyles();
    const { mobilePlatform, buildProfile, isNewApp, artifactUrl } = publication;
    const isAndroid = mobilePlatform === 'android';
    const isIOS = mobilePlatform === 'ios';
    const isProduction = buildProfile === 'store-production';
    const isStaging = buildProfile === 'store-staging';

    const expoSlug = mapExpoAccountNameToUrlSlug(publication.expoAccountName);
    const buildUrl = publication.buildId
        ? `https://expo.dev/accounts/${expoSlug}/projects/${_.get(publication, 'metadata.appScheme')}/builds/${publication.buildId}`
        : null;

    if (isStaging) {
        return (
            <Grid container direction="column" spacing={2}>
                <Grid item>
                    <h2 className={classes.sectionText}>Ações necessárias</h2>
                    {isAndroid ? (
                        <>
                            <Typography variant="body1" gutterBottom>
                                1 - Baixe o arquivo APK e instale em um dispositivo Android para teste.
                            </Typography>
                            {artifactUrl && (
                                <Box mt={1}>
                                    <Button variant="outlined" color="primary" size="small" href={artifactUrl} target="_blank" rel="noreferrer">
                                        ⬇ Baixar APK
                                    </Button>
                                </Box>
                            )}
                        </>
                    ) : (
                        <>
                            <Typography variant="body1" gutterBottom>
                                Escaneie o QR Code do Expo ou acesse o link direto para instalar o app de teste nos dispositivos cadastrados no projeto Expo.
                            </Typography>
                            {buildUrl && (
                                <Box mt={2}>
                                    <QRCodeSVG value={buildUrl} size={100} />
                                    <Box mt={1}>
                                        <a href={buildUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#1565c0' }}>
                                            Abrir no Expo
                                        </a>
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </Grid>
            </Grid>
        );
    }

    // production
    if (isAndroid) {
        return (
            <Grid container direction="column" spacing={2}>
                <Grid item>
                    <h2 className={classes.sectionText}>Ações necessárias</h2>
                    {isNewApp ? (
                        <>
                            <Typography variant="body1" gutterBottom>
                                1 - Baixe o arquivo <strong>.aab</strong> abaixo.
                            </Typography>
                            {artifactUrl && (
                                <Box mt={1} mb={2}>
                                    <Button variant="outlined" color="primary" size="small" href={artifactUrl} target="_blank" rel="noreferrer">
                                        ⬇ Baixar .aab
                                    </Button>
                                </Box>
                            )}
                            <Typography variant="body1" gutterBottom>
                                2 - Acesse o Google Play Console, crie um release de <strong>produção</strong> e envie o arquivo .aab.
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                3 - Envie para revisão.
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="body1" gutterBottom>
                            O aplicativo foi enviado automaticamente para a Google Play via auto-submit. Nenhuma ação manual é necessária — confirme a conclusão para registrar o status.
                        </Typography>
                    )}
                </Grid>
            </Grid>
        );
    }

    // iOS production
    return (
        <Grid container direction="column" spacing={2}>
            <Grid item>
                <h2 className={classes.sectionText}>Ações necessárias</h2>
                {isNewApp && (
                    <Typography variant="body1" gutterBottom>
                        1 - Configure o envio de notificações push no Firebase para este aplicativo.
                    </Typography>
                )}
                <Typography variant="body1" gutterBottom>
                    {isNewApp ? '2' : '1'} - Acesse a App Store Connect, selecione a compilação enviada pelo Expo e envie o aplicativo para revisão.
                </Typography>
            </Grid>
        </Grid>
    );
}

export default function FinishAppPublication({ publication, isOpen, onClose, onDataUpdate }) {
    const classes = useStyles();
    const [confirmed, setConfirmed] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleFinish = () => {
        setLoading(true);
        mobilizeiAppPublicationsService.finishAppPublication(publication.id)
            .then(() => {
                onDataUpdate();
                onClose();
            })
            .catch((e) => {
                alert(`Erro ao finalizar a publicação: ${e?.message || JSON.stringify(e)}`);
            })
            .finally(() => setLoading(false));
    };

    return (
        <ReactModal isOpen={isOpen} onRequestClose={onClose} style={customStyles}>
            <Box display="flex" flexDirection="column" justifyContent="space-between" style={{ height: '100%' }}>
                <Grid container>
                    <h2 className={classes.title}>Concluir publicação</h2>
                    <p style={{ color: '#555', margin: '0 0 16px 0', width: '100%' }}>
                        {_.get(publication, 'metadata.appName', publication.storeId)} — {publication.platformId} — {publication.mobilePlatform}
                    </p>

                    <Instructions publication={publication} />

                    <Box display="flex" justifyContent="center" pt={3} width="100%">
                        <FormControlLabel
                            control={<Checkbox onChange={e => setConfirmed(e.target.checked)} />}
                            label="Confirmo que realizei as ações acima."
                        />
                    </Box>
                </Grid>

                {loading
                    ? <Box className={classes.actions}><CircularProgress /></Box>
                    : <Box className={classes.actions}>
                        <Button variant="outlined" onClick={onClose}>Cancelar</Button>
                        <Button
                            disabled={!confirmed}
                            variant="contained"
                            color="primary"
                            onClick={handleFinish}
                        >
                            Finalizar publicação
                        </Button>
                    </Box>
                }
            </Box>
        </ReactModal>
    );
}
